import React, { PropsWithChildren } from 'react';
import styled from 'styled-components/native';
import Colors from '../constants/Colors';
import Theme from '../constants/Theme';
import elementIDs from '../../elementIDs';

export interface TypographyProps {
  variant:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'body'
    | 'overline'
    | 'p'
    | 'b'
    | 'caption'
    | 'subCaption'
    | 'i';
}

interface StyledTextProps {
  center?: boolean;
  padding?: boolean | number;
  flex?: boolean;
  color?: 'light' | 'medium' | 'dark' | string;
}

const StyledText = styled.Text<StyledTextProps>`
  ${p => (p.flex ? `flex: 1` : '')};
  flex-wrap: wrap; /* android needs this */
  color: ${p =>
    p.color === 'dark'
      ? Colors.darkText
      : p.color === 'light'
        ? Colors.lightText
        : p.color === 'medium'
          ? Colors.mediumText
          : typeof p.color === 'string'
            ? p.color
            : Colors.mediumText};
  text-align: ${p => (p.center ? 'center' : 'left')};
  padding-bottom: ${p =>
    typeof p.padding === 'undefined'
      ? 0
      : typeof p.padding === 'boolean'
        ? 24
        : p.padding * Theme.spacing}px;
`;

// Disable Font Scaling
StyledText.defaultProps = {
  ...(StyledText.defaultProps || {}),
  allowFontScaling: false
};

export const H1 = styled(StyledText)`
  font-family: 'WorkSans-SemiBold';
  font-size: 65px;
  line-height: 76px;
`;

export const H2 = styled(StyledText)`
  font-family: 'WorkSans-SemiBold';
  font-size: 34px;
  line-height: 40px;
`;

export const H3 = styled(StyledText)`
  font-family: 'WorkSans-SemiBold';
  font-size: 28px;
  line-height: 40px;
`;

export const H4 = styled(StyledText)`
  font-family: 'WorkSans-SemiBold';
  font-size: 22px;
  line-height: 26px;
`;

export const H5 = styled(StyledText)`
  font-family: 'WorkSans-SemiBold';
  font-size: 18px;
  line-height: 21px;
`;

export const Overline = styled(StyledText)`
  font-family: 'WorkSans-Regular';
  font-size: 10px;
  line-height: 12px;
  text-transform: uppercase;
`;

export const Subtitle = styled(StyledText)`
  font-family: 'WorkSans-SemiBold';
  font-size: 18px;
  line-height: 21px;
`;

export const P = styled(StyledText)`
  font-family: 'WorkSans-Regular'; /* Font Weight 400 */
  font-size: 16px;
  line-height: 18px;
`;

export const B = styled(StyledText)`
  font-family: 'WorkSans-SemiBold'; /* Font Weight 600 */
  font-size: 16px;
  line-height: 18px;
`;

export const Caption = styled(StyledText)`
  font-family: 'WorkSans-Regular';
  font-size: 14px;
  line-height: 16px;
`;

export const SubCaption = styled(StyledText)`
  font-family: 'WorkSans-Regular'; /* Font Weight 400 */
  font-size: 12px;
  line-height: 14px;
`;

export const I = styled(StyledText)`
  font-family: 'WorkSans-SemiBold'; /* Font Weight 600 */
  font-size: 14.4px;
  letter-spacing: -0.08px;
  line-height: 19.8px;
`;

const Typography: React.FC<TypographyProps & PropsWithChildren> = ({
  variant,
  children
}) => {
  switch (variant) {
    case 'h1':
      return <H1 testID={elementIDs.formSubtitleButtonID}>{children}</H1>;
    case 'h2':
      return <H2 testID={elementIDs.formSubtitleButtonID}>{children}</H2>;
    case 'h3':
      return <H3 testID={elementIDs.formSubtitleButtonID}>{children}</H3>;
    case 'h4':
      return <H4 testID={elementIDs.formSubtitleButtonID}>{children}</H4>;
    case 'h5':
      return <H5 testID={elementIDs.formSubtitleButtonID}>{children}</H5>;
    case 'overline':
      return (
        <Overline testID={elementIDs.formSubtitleButtonID}>{children}</Overline>
      );
    case 'p':
      return <P testID={elementIDs.formSubtitleButtonID}>{children}</P>;
    case 'b':
      return <B testID={elementIDs.formSubtitleButtonID}>{children}</B>;
    case 'caption':
      return (
        <Caption testID={elementIDs.formSubtitleButtonID}>{children}</Caption>
      );
    case 'subCaption':
      return (
        <SubCaption testID={elementIDs.formSubtitleButtonID}>
          {children}
        </SubCaption>
      );
    case 'i':
      return <I testID={elementIDs.formSubtitleButtonID}>{children}</I>;
    default:
      return <P testID={elementIDs.formSubtitleButtonID}>{children}</P>;
  }
};

export default Typography;
